import { ColDef, ColumnState } from 'ag-grid-community';
import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { autoSizeAllColumns } from 'src/utils/grid/ColumnSizeUtils';

// 공통 그리드 컨텍스트 메뉴 래퍼 props
interface GridContextMenuWrapperProps<TData extends { inserted?: boolean } = any> {
	children: React.ReactNode;
	gridRef: React.RefObject<AgGridReact<TData> | null>;
	// gridWrapperRef 타입을 더 유연하게 변경
	gridWrapperRef?: React.RefObject<HTMLDivElement> | React.MutableRefObject<HTMLDivElement | null>;
	paddedGridData: TData[];
	setPaddedGridData?: (gridData: TData[]) => void;
	initSelected: () => void;
	allSelected?: () => void;
	baseColumnDefs?: ColDef<TData>[];
	setColumnState?: (state: ColumnState[] | null) => void;
	localStorageKey?: string;
	menuItems?: Array<{
		label: string;
		onClick: () => void;
	}>;
	// 행 드래그 옵션
	enableRowDrag?: boolean;
	dragEntireRow?: boolean;
	orderIndexField?: keyof TData | string;
	orderIndexSeqField?: keyof TData | string;
	onAfterReorder?: (list: TData[]) => void;
	// 추가 기능 옵션
	enableDeleteAddedRow?: boolean;
	enableColumnReset?: boolean;
	enableAutoSize?: boolean;
	enableSelectionReset?: boolean;
	// 높이 설정 옵션
	height?: string | number;
}

export const GridContextMenuWrapper = <TData extends { inserted?: boolean } = any>({ 
	children, 
	gridRef,	
	gridWrapperRef,
	paddedGridData, 
	setPaddedGridData,
	initSelected,
	allSelected = undefined,
	baseColumnDefs,
	setColumnState,
	localStorageKey,
	menuItems = [], 
	enableRowDrag = false,
	dragEntireRow = true,
	orderIndexField = 'orderIndex',
	orderIndexSeqField = 'orderIndexSeq',
	onAfterReorder,
	enableDeleteAddedRow = true,
	enableColumnReset = true,
	enableAutoSize = true,
	enableSelectionReset = true,
	height = '610px',  // GridContextWrapper와 동일하게 기본값 설정
}: GridContextMenuWrapperProps<TData>) => {
	const wrapperRef = useRef<HTMLDivElement>(null);
	
	// 컨텍스트 메뉴 상태
	const [contextMenu, setContextMenu] = useState<{
		visible: boolean;
		x: number;
		y: number;
	}>({
		visible: false,
		x: 0,
		y: 0
	});

	// 컨텍스트 메뉴 닫기
	const closeContextMenu = useCallback(() => { 
		setContextMenu({ visible: false, x: 0, y: 0 }); 
	}, []);

	// 우클릭 이벤트 처리
	const handleContextMenu = useCallback((e: React.MouseEvent) => {
		e.preventDefault();
		setContextMenu({
			visible: true,
			x: e.clientX,
			y: e.clientY
		});
	}, []);

	// 클릭 시 컨텍스트 메뉴 닫기
	useEffect(() => {
		const handleClick = () => closeContextMenu();
		if (contextMenu.visible) {
			document.addEventListener('click', handleClick);
			return () => document.removeEventListener('click', handleClick);
		}
	}, [contextMenu.visible, closeContextMenu]);

	// 컬럼 초기화 함수
	const resetColumnState = useCallback(() => {
		if (!gridRef || !baseColumnDefs || !setColumnState) return;

		// 1. 로컬스토리지에서 컬럼 상태 제거
		if (localStorageKey) {
			localStorage.removeItem(localStorageKey);
		}
		
		// 2. 스토어의 columnState를 null로 설정
		setColumnState(null);
		
		// 3. 컴포넌트 리렌더링 후 AG Grid 초기화를 위해 setTimeout 사용
		setTimeout(() => {
			if (gridRef.current?.api) {
				// baseColumnDefs에서 모든 컬럼 ID 추출
				const allColumnIds = baseColumnDefs
					.map(col => col.colId)
					.filter((colId): colId is string => colId !== undefined);
				
				// 모든 컬럼을 강제로 표시
				gridRef.current.api.setColumnsVisible(allColumnIds, true);
				
				// 컬럼 상태 초기화
				gridRef.current.api.resetColumnState();
			}
		}, 0);
		
		closeContextMenu();
	}, [gridRef, baseColumnDefs, setColumnState, localStorageKey, closeContextMenu]);

	// 컬럼 너비 자동 조정 후 상태 저장
	const saveColumnStateAfterResize = useCallback(() => {
		if (gridRef?.current?.api && localStorageKey && setColumnState) {
			const state = gridRef.current.api.getColumnState();
			if (state) {
				localStorage.setItem(localStorageKey, JSON.stringify(state));
				setColumnState(state);
			}
		}
	}, [gridRef, localStorageKey, setColumnState]);

	// 추가한 행 삭제 함수
	const deleteAddedRow = useCallback(() => {
		if (!gridRef?.current || !setPaddedGridData) return;

		const newPaddedGridData: TData[] = [];

		// inserted 속성이 있는 데이터만 필터링하여 처리
		const dataWithInserted = paddedGridData.filter((item): item is TData & { inserted: boolean } => 
			'inserted' in item
		);
		
		// inserted 데이터가 없으면 처리하지 않음
		if (dataWithInserted.length === 0) {
			return;
		}
		
		// 그리드에 inserted가 모두 true인 경우 삭제 불가, 첫행만 남김
		const isAllInserted = dataWithInserted.every(item => item.inserted);
		if(isAllInserted) {
			newPaddedGridData.push(paddedGridData[0]);
		}else{
			paddedGridData.forEach(item => {
				if('inserted' in item && (item as any).inserted) {
					return;
				}
				newPaddedGridData.push(item);
			});
		}
		
		setPaddedGridData(newPaddedGridData);
	}, [gridRef, paddedGridData, setPaddedGridData]);

	// 행 드래그: 드랍 후 순서 재계산 처리
	const handleRowDragEnd = useCallback((e: any) => {
		if (!enableRowDrag) return;
		
		const api = gridRef.current?.api || e?.api;
		if (!api) return;
		
		if (api.applyColumnState) {
			api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
		}
		
		const reordered: TData[] = [];
		api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
			if (!node?.data) return;
			reordered.push({
				...node.data,
				[(orderIndexField as string)]: idx + 1,
				[(orderIndexSeqField as string)]: 0,
				// 표준 필드도 함께 갱신하여 공통 postSortRows 로직과 정렬 충돌 방지
				orderIndex: idx + 1,
				orderIndexSeq: 0,
			} as TData);
		});
		
		if (setPaddedGridData) setPaddedGridData(reordered);
		if (onAfterReorder) onAfterReorder(reordered);
	}, [enableRowDrag, gridRef, setPaddedGridData, orderIndexField, orderIndexSeqField, onAfterReorder]);

	// children에 드래그 props 주입
	const enhancedChildren = React.useMemo(() => {
		if (!enableRowDrag) return children;
		
		const injector = (child: any) => {
			if (!React.isValidElement(child)) return child;
			
			const prevOnRowDragEnd = (child.props as any)?.onRowDragEnd;
			return React.cloneElement(child as any, {
				rowDragManaged: true as any,
				rowDragEntireRow: dragEntireRow as any,
				onRowDragEnd: (e: any) => {
					if (typeof prevOnRowDragEnd === 'function') prevOnRowDragEnd(e);
					handleRowDragEnd(e);
				},
			});
		};
		
		return React.Children.map(children as any, injector);
	}, [children, enableRowDrag, dragEntireRow, handleRowDragEnd]);

	// 기본 메뉴 아이템들 (옵션에 따라 동적 생성)
	const defaultMenuItems = [];
	
	if (enableColumnReset && gridRef && baseColumnDefs && setColumnState) {
		defaultMenuItems.push({
			label: '컬럼 초기화',
			onClick: resetColumnState
		});
	}
	
	if (enableAutoSize && gridRef) {
		defaultMenuItems.push({
			label: '너비 맞추기',
			onClick: () => {
				if (!gridRef?.current) return;
				autoSizeAllColumns(gridRef.current.api, { saveStateCallback: saveColumnStateAfterResize });
			}
		});
	}
	
	if (enableSelectionReset && gridRef) {
		defaultMenuItems.push({
			label: '선택 행 초기화',
			onClick: () => {
				if (!gridRef?.current) return;
				initSelected();
			}
		});
	}
	
	if (enableDeleteAddedRow && setPaddedGridData) {
		defaultMenuItems.push({
			label: '추가한 행 삭제',
			onClick: deleteAddedRow
		});
	}

	const allMenuItems = [...defaultMenuItems, ...menuItems];

	return (
		<div ref={gridWrapperRef} style={{ height, width: '100%' }}>
			<div 
				ref={wrapperRef}
				className="ag-theme-alpine w-full h-full flex flex-col overflow-y-auto z-0 relative" 
				onContextMenu={handleContextMenu}
			>
				{enhancedChildren}
				
				{/* 커스텀 컨텍스트 메뉴 */}
				{contextMenu.visible && allMenuItems.length > 0 && (
					<>
						<div className="absolute bg-white border border-gray-300 shadow-lg rounded-md py-1 z-50"
							style={{ left: 0, top: 0, width: '100%', height: '100%', backgroundColor: 'rgba(255, 255, 255, 0)', }}
							onClick={(e) => { e.stopPropagation(); closeContextMenu(); }}
						></div>
						<div
							className="fixed bg-white border border-gray-300 shadow-lg rounded-md py-1 z-50"
							style={{
								left: contextMenu.x,
								top: contextMenu.y,
								minWidth: '120px'
							}}
						>
							{allMenuItems.map((item, index) => (
								<div
									key={index}
									className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
									onClick={() => {
										item.onClick();
										closeContextMenu();
									}}
								>
									{item.label}
								</div>
							))}
						</div>
					</>
				)}
			</div>
		</div>
	);
};
